<template>
  <section class="event-month-calendar panel">
    <div class="calendar-header">
      <button type="button" class="btn btn-secondary calendar-nav" @click="prevMonth">‹</button>
      <h2 class="calendar-title">{{ monthLabel(currentMonth) }}</h2>
      <div class="calendar-header-actions">
        <button type="button" class="btn btn-secondary calendar-nav" @click="nextMonth">›</button>
        <button
          type="button"
          class="btn btn-secondary calendar-toggle"
          :aria-expanded="expanded"
          :aria-label="expanded ? 'Свернуть календарь' : 'Развернуть календарь'"
          @click="expanded = !expanded"
        >
          <span aria-hidden="true">{{ expanded ? '▾' : '▸' }}</span>
        </button>
      </div>
    </div>

    <div v-show="expanded">
      <div class="calendar-weekdays">
        <span v-for="day in weekdays" :key="day">{{ day }}</span>
      </div>

      <div class="calendar-grid">
      <button
        v-for="cell in calendarCells"
        :key="cell.key"
        type="button"
        class="calendar-cell"
        :class="{
          'calendar-cell--outside': !cell.inMonth,
          'calendar-cell--today': cell.isToday,
        }"
        @click="$emit('select-day', cell.date)"
      >
        <span class="calendar-day-number">{{ cell.dayNumber }}</span>
        <ul v-if="cell.events.length" class="calendar-events">
          <li
            v-for="event in cell.events.slice(0, maxEventsVisible)"
            :key="event.id"
            class="calendar-event"
            @click.stop="$emit('select-event', event.id)"
          >
            {{ event.name }}
          </li>
          <li v-if="cell.events.length > maxEventsVisible" class="calendar-event calendar-event--more">
            +{{ cell.events.length - maxEventsVisible }}
          </li>
        </ul>
      </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { monthLabel } from '../utils/eventFormat'

const props = defineProps({
  events: {
    type: Array,
    default: () => [],
  },
  month: {
    type: Date,
    default: () => new Date(),
  },
})

const emit = defineEmits(['select-event', 'select-day', 'update:month'])

const weekdays = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']
const currentMonth = ref(new Date(props.month))
const expanded = ref(true)
const maxEventsVisible = ref(3)

const mobileMediaQuery = typeof window !== 'undefined'
  ? window.matchMedia('(max-width: 767px)')
  : null

function updateMaxEventsVisible() {
  maxEventsVisible.value = mobileMediaQuery?.matches ? 2 : 3
}

onMounted(() => {
  updateMaxEventsVisible()
  mobileMediaQuery?.addEventListener('change', updateMaxEventsVisible)
})

onUnmounted(() => {
  mobileMediaQuery?.removeEventListener('change', updateMaxEventsVisible)
})

watch(
  () => props.month,
  (value) => {
    currentMonth.value = new Date(value)
  }
)

const calendarCells = computed(() => {
  const year = currentMonth.value.getFullYear()
  const month = currentMonth.value.getMonth()
  const firstDay = new Date(year, month, 1)
  const startOffset = (firstDay.getDay() + 6) % 7
  const gridStart = new Date(year, month, 1 - startOffset)
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart)
    date.setDate(gridStart.getDate() + index)
    date.setHours(0, 0, 0, 0)

    const dayEnd = new Date(date)
    dayEnd.setHours(23, 59, 59, 999)

    const dayEvents = props.events.filter((event) => {
      const starts = new Date(event.starts_at)
      const ends = new Date(event.ends_at)
      return starts <= dayEnd && ends >= date
    })

    return {
      key: date.toISOString(),
      date,
      dayNumber: date.getDate(),
      inMonth: date.getMonth() === month,
      isToday: date.getTime() === today.getTime(),
      events: dayEvents,
    }
  })
})

function prevMonth() {
  currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() - 1, 1)
  emit('update:month', currentMonth.value)
}

function nextMonth() {
  currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() + 1, 1)
  emit('update:month', currentMonth.value)
}

defineExpose({ currentMonth })
</script>

<style scoped>
.event-month-calendar {
  margin-bottom: 1.5rem;
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.calendar-header-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.calendar-title {
  margin: 0;
  font-size: 1.25rem;
  text-transform: capitalize;
  flex: 1;
  min-width: 0;
  text-align: center;
}

.calendar-nav,
.calendar-toggle {
  min-width: 2.5rem;
  padding-inline: 0.75rem;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
  margin-bottom: 0.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #64748b;
  text-align: center;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.25rem;
}

.calendar-cell {
  min-height: 5.5rem;
  padding: 0.375rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #fff;
  text-align: left;
  cursor: pointer;
}

.calendar-cell--outside {
  background: #f8fafc;
  color: #94a3b8;
}

.calendar-cell--today {
  border-color: #2563eb;
}

.calendar-day-number {
  display: block;
  font-size: 0.8125rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.calendar-events {
  list-style: none;
  margin: 0;
  padding: 0;
}

.calendar-event {
  font-size: 0.6875rem;
  line-height: 1.2;
  padding: 0.125rem 0.25rem;
  margin-bottom: 0.125rem;
  border-radius: 0.25rem;
  background: #dbeafe;
  color: #1e40af;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.calendar-event--more {
  background: #f1f5f9;
  color: #64748b;
}

@media (max-width: 767px) {
  .calendar-title {
    font-size: 1rem;
  }

  .calendar-weekdays {
    font-size: 0.6875rem;
    gap: 0.125rem;
  }

  .calendar-grid {
    gap: 0.125rem;
  }

  .calendar-cell {
    min-height: 3.5rem;
    padding: 0.25rem;
  }

  .calendar-day-number {
    font-size: 0.75rem;
  }

  .calendar-event {
    font-size: 0.625rem;
    padding: 0.0625rem 0.125rem;
  }
}
</style>
